import React, { useState, useEffect } from "react";
import {
  Grid, Box, Container, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Button, Typography,
  Select, MenuItem, TextField, Autocomplete, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox
} from "@mui/material";

import {
  getAvailableBallkidsForDay,
  getAllCourtAssignments,
  markBallkidAsAssigned,
  unassignBallkid,
  getAttendanceComment
} from "../firebaseService";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];

const BallkinderAllocation = () => {
  const { daysOfWeek } = useTournamentConfig();
  const [selectedCourt, setSelectedCourt] = useState(courts[0]);
  const [availableBallkids, setAvailableBallkids] = useState([]);
  const [allBallkidData, setAllBallkidData] = useState({}); // 🆕 Map für vollständige Daten
  const [courtAssignments, setCourtAssignments] = useState({});
  const [selectedBallkidA, setSelectedBallkidA] = useState(null);
  const [selectedBallkidB, setSelectedBallkidB] = useState(null);
  const [selectedBallkidC, setSelectedBallkidC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [filterNet, setFilterNet] = useState(false);

  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const availableKids = await getAvailableBallkidsForDay(selectedDay);
      const assignments = await getAllCourtAssignments(selectedDay);

      // Map erstellen: name → ballkid object
      const bkMap = {};
      availableKids.forEach((bk) => {
        if (bk && bk.id && bk.name) {
          bkMap[bk.name] = bk;
        }
      });
      setAllBallkidData(bkMap);

      // Entferne alle schon zugewiesenen IDs
      const assignedKids = new Set();
      Object.values(assignments).forEach((crew) => {
        crew.forEach((name) => assignedKids.add(name));
      });

      const filteredAvailableKids = availableKids.filter((bk) => bk && !assignedKids.has(bk.name));
      setAvailableBallkids(filteredAvailableKids);
      setCourtAssignments(assignments);

      setLoading(false);
    };

    fetchData();
  }, [selectedDay, selectedCourt]);

  const handleOpenCommentDialog = async () => {
    const loadedComment = await getAttendanceComment();
    setComment(loadedComment);
    setCommentDialogOpen(true);
  };

  const handleCloseCommentDialog = () => {
    setCommentDialogOpen(false);
  };

  const handleAddBallkid = async (crew, ballkid) => {
    if (!ballkid || !ballkid.id) return;

    await markBallkidAsAssigned(ballkid.id, selectedDay, selectedCourt, crew);

    setAvailableBallkids((prev) => prev.filter((bk) => bk.id !== ballkid.id));
    setCourtAssignments((prev) => ({
      ...prev,
      [`${selectedDay}_${selectedCourt}_${crew}`]: [...(prev[`${selectedDay}_${selectedCourt}_${crew}`] || []), ballkid.name],
    }));

    if (crew === "crewA") setSelectedBallkidA(null);
    if (crew === "crewB") setSelectedBallkidB(null);
    if (crew === "crewC") setSelectedBallkidC(null);
  };

  const handleRemoveBallkid = async (crew, ballkidName) => {
    await unassignBallkid(ballkidName, selectedDay, selectedCourt, crew);

    setCourtAssignments((prev) => ({
      ...prev,
      [`${selectedDay}_${selectedCourt}_${crew}`]: prev[`${selectedDay}_${selectedCourt}_${crew}`].filter((name) => name !== ballkidName)
    }));

    const restored = allBallkidData[ballkidName];
    if (restored && restored.id) {
      setAvailableBallkids((prev) => {
        // Keine Duplikate hinzufügen
        if (prev.some((bk) => bk.id === restored.id)) return prev;
        return [...prev, restored];
      });
    } else {
      console.warn(`⚠️ Ballkind '${ballkidName}' konnte nicht vollständig wiederhergestellt werden`);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom style={{ paddingTop: 20 }}>
        Einteilung
      </Typography>

      <Select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}>
        {daysOfWeek.map((day) => (
          <MenuItem key={day} value={day}>{day}</MenuItem>
        ))}
      </Select>

      <Button variant="contained" color="secondary" onClick={handleOpenCommentDialog} style={{ marginBottom: 20, marginLeft: 20 }}>
        Kommentar anzeigen
      </Button>

      <Tabs value={selectedCourt} onChange={(e, newValue) => setSelectedCourt(newValue)}  variant="scrollable"
  scrollButtons="auto">
        {courts.map((court) => (
          <Tab key={court} label={court} value={court} />
        ))}
      </Tabs>

      {loading ? (
        <CircularProgress style={{ marginTop: 20 }} />
      ) : (
        <>
          <FormControlLabel
            control={<Checkbox checked={filterNet} onChange={(e) => setFilterNet(e.target.checked)} />}
            label="Nur Netz-Kinder anzeigen"
            style={{ marginTop: 10 }}
          />
          {["A", "B", "C"].map((crewLabel) => {
            const crewKey = `crew${crewLabel}`;
            const selectedBallkid = {
              A: selectedBallkidA,
              B: selectedBallkidB,
              C: selectedBallkidC
            }[crewLabel];
            const setSelectedBallkid = {
              A: setSelectedBallkidA,
              B: setSelectedBallkidB,
              C: setSelectedBallkidC
            }[crewLabel];

            return (
              <div key={crewLabel}>
                <Typography variant="h6" style={{ marginTop: 20 }}>Crew {crewLabel}</Typography>
                <Autocomplete
                  options={filterNet ? availableBallkids.filter(bk => bk.canNet) : availableBallkids}
                  getOptionLabel={(option) => {
                    let label = option.name;
                    if (option.canNet) label += " [Netz]";
                    if (option.from) label += ` (ab ${option.from})`;
                    return label;
                  }}
                  value={selectedBallkid}
                  onChange={(event, newValue) => setSelectedBallkid(newValue)}
                  renderInput={(params) => <TextField {...params} label="Mitglied hinzufuegen" variant="outlined" />}
                />
                <Button variant="contained" onClick={() => handleAddBallkid(crewKey, selectedBallkid)} style={{ marginTop: 10 }}>
                  Hinzufügen
                </Button>
                <TableContainer component={Paper} style={{ marginTop: 20 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(courtAssignments[`${selectedDay}_${selectedCourt}_${crewKey}`] || []).map((name, index) => {
                        const bkData = allBallkidData[name];
                        const fromTime = bkData?.from;
                        const canNet = bkData?.canNet;
                        return (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {name || "Unbekannt"}
                            {canNet && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: "info.main" }}>
                                [Netz]
                              </Typography>
                            )}
                            {fromTime && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: "warning.main" }}>
                                (ab {fromTime})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button onClick={() => handleRemoveBallkid(crewKey, name)} variant="contained" color="secondary">
                              Entfernen
                            </Button>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            );
          })}
        </>
      )}

      <Dialog open={commentDialogOpen} onClose={handleCloseCommentDialog} fullWidth maxWidth="md">
        <DialogTitle>Kommentar</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Kommentar"
            type="text"
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            value={comment}
            disabled
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommentDialog} color="primary">Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BallkinderAllocation;
